import React, { useState, useEffect } from 'react';
import { getMyTeams, listTenantTeams, grantRecordTeamAccess } from '../api/chatAI';
import useAuthStore from '../store/authStore';

export default function TeamAccessModal({ recordId, alreadyGrantedTeamIds, defaultAccessLevel, onClose, onSuccess }) {
  const { user } = useAuthStore();
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [accessLevel, setAccessLevel] = useState(defaultAccessLevel || 'write');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [myTeams, allTeams] = await Promise.all([getMyTeams(), listTenantTeams()]);
        if (cancelled) return;

        const granted = new Set(alreadyGrantedTeamIds || []);
        let pool;
        if (user?.is_admin) {
          pool = allTeams.filter(t => !granted.has(t.id) && t.is_active !== false);
        } else {
          const myTeamIds = new Set(myTeams.map(t => t.id));
          pool = allTeams.filter(t => myTeamIds.has(t.id) && !granted.has(t.id) && t.is_active !== false);
        }
        setAvailableTeams(pool);
        if (pool.length > 0) setSelectedTeamId(pool[0].id);
      } catch (e) {
        if (!cancelled) setError('Failed to load teams');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [alreadyGrantedTeamIds, user?.is_admin]);

  const handleSubmit = async () => {
    if (!selectedTeamId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await grantRecordTeamAccess(recordId, {
        team_id: selectedTeamId,
        access_level: accessLevel,
        source: 'manual',
      });
      window.dispatchEvent(new CustomEvent('teamAccessChanged', { detail: { record_id: recordId } }));
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response.data?.error || "You don't have permission to share this case.");
      } else {
        setError(err.response?.data?.error || 'Failed to grant access. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()} onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <p className="text-sm font-semibold text-white">Share case with team</p>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-lg leading-none">x</button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-xs text-gray-500 text-center py-4">Loading teams...</p>
          ) : availableTeams.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">
              No teams available to share with. You're already sharing this case with every team you're a member of.
            </p>
          ) : (
            <>
              {/* Team dropdown */}
              <div>
                <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide block mb-1.5">Team</label>
                <select
                  value={selectedTeamId}
                  onChange={e => { setSelectedTeamId(e.target.value); setError(null); }}
                  className="w-full bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-amber-600"
                >
                  {availableTeams.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Access level radios */}
              <div>
                <label className="text-[11px] text-gray-400 font-medium uppercase tracking-wide block mb-1.5">Access level</label>
                <div className="flex gap-3">
                  {['read', 'write', 'full'].map(level => (
                    <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="access_level"
                        value={level}
                        checked={accessLevel === level}
                        onChange={() => { setAccessLevel(level); setError(null); }}
                        className="accent-amber-500"
                      />
                      <span className="text-xs text-gray-300 capitalize">{level}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-gray-600 mt-1">
                  Defaults to {defaultAccessLevel || 'write'} per firm settings
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-gray-800">
          <button
            onClick={() => handleSubmit()}
            disabled={submitting || !selectedTeamId || availableTeams.length === 0}
            className="flex-1 py-2.5 text-xs font-semibold rounded-lg disabled:opacity-50 transition-colors"
            style={{
              background: 'linear-gradient(135deg, #c9a84c, #a67c32)',
              color: '#fff8e6',
              border: '1px solid #d4b060',
              cursor: submitting || !selectedTeamId ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Sharing...' : 'Share'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-xs rounded-lg transition-colors"
            style={{ background: 'transparent', color: '#d94f4f', border: '1px solid #7a2424', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
